
import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Calculator, DollarSign } from 'lucide-react';
import { fadeIn, staggerContainer, getReducedMotionVariants } from '../utils/motion';
import { EnhancedCard } from '@/components/ui/enhanced-card';
import { Card, CardContent } from '@/components/ui/card';

interface SmartBuyPageProps {
  reducedMotion: boolean;
}

const SmartBuyPage: React.FC<SmartBuyPageProps> = ({ reducedMotion }) => {
  const coreTools = [
    {
      icon: Calculator,
      name: "Mortgage Calculator",
      stat: "5,000+",
      description: "Advanced calculations completed monthly"
    },
    {
      icon: Brain,
      name: "SmartBuy Analyzer",
      stat: "10,000+",
      description: "Scenario comparisons analyzed"
    },
    {
      icon: DollarSign,
      name: "Unified Buying Power",
      stat: "99.9%",
      description: "Qualification accuracy rate"
    }
  ];

  return (
    <div className="space-y-10">
      {/* Header */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-4"
      >
        <h2 className="text-3xl font-bold">Core Analysis Tools</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Powerful calculators and comparison tools that form the foundation of confident homebuying decisions
        </p>
      </motion.div>

      {/* Tool Stats */}
      <motion.div
        variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        {coreTools.map((tool, index) => (
          <motion.div
            key={tool.name}
            variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.4), reducedMotion)}
          >
            <Card>
              <CardContent className="p-6 text-center space-y-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto">
                  <tool.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">{tool.name}</h3>
                <div className="text-3xl font-bold text-primary">{tool.stat}</div>
                <p className="text-sm text-muted-foreground">{tool.description}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Detailed Tool Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-8"
        variants={getReducedMotionVariants(staggerContainer(0.2, 0.1), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
      {/* Left Column - Tool Description */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('right', 0.1), reducedMotion)}
        className="flex flex-col justify-center"
      >
        <EnhancedCard className="bg-slate-900 dark:bg-black/70 text-white p-6 h-full">
          <div className="space-y-6 flex flex-col h-full">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-500 p-3 rounded-lg">
                <Brain className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold">SmartBuy Analyzer</h2>
            </div>
            
            <div className="flex-grow space-y-4">
              <h3 className="text-xl font-semibold text-blue-300">Deep Financial Analysis</h3>
              <p className="text-gray-300">
                Our SmartBuy Analyzer tool provides comprehensive mortgage qualification insights, giving
                you confidence before making offers on properties.
              </p>
              
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                  <span>Real-time debt-to-income ratio calculations</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                  <span>FHA, VA, and conventional loan comparisons</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                  <span>Precise monthly payment breakdowns</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                  <span>Automated underwriting approval predictions</span>
                </li>
              </ul>
            </div>
          </div>
        </EnhancedCard>
      </motion.div>

      {/* Right Column - Screenshot */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('left', 0.3), reducedMotion)}
        className="flex items-center justify-center"
      >
        <EnhancedCard className="w-full overflow-hidden" outerGlow>
          <img 
            src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6"
            alt="SmartBuy Analyzer Results Screen - Advanced mortgage qualification interface"
            className="w-full h-auto object-cover rounded-t-lg"
            loading="lazy"
          />
          <div className="p-4 bg-white/5 backdrop-blur-sm">
            <h4 className="font-semibold">Actionable Results</h4>
            <p className="text-sm text-muted-foreground">
              Get clear insights into your mortgage qualification with our easy-to-understand results dashboard.
            </p>
          </div>
        </EnhancedCard>
      </motion.div>
      </motion.div>
    </div>
  );
};

export default SmartBuyPage;
